import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Mic, Play, Square, Volume2, Music, Save, RefreshCw, 
  ChevronDown, Upload, Sliders, Building2 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TextToVoice = () => {
  const { isLight } = useTheme();
  // Voice generation state
  const [text, setText] = useState('');
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAudio, setGeneratedAudio] = useState(null);
  
  // Voice settings
  const [stability, setStability] = useState(0.5);
  const [similarityBoost, setSimilarityBoost] = useState(0.75);
  const [style, setStyle] = useState(0);
  
  // Background music state
  const [songs, setSongs] = useState([]);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [uploadedBackground, setUploadedBackground] = useState(null);
  const [backgroundSource, setBackgroundSource] = useState('none'); // none, library, upload
  
  // Volume & fade controls
  const [voiceVolume, setVoiceVolume] = useState(1.0);
  const [backgroundVolume, setBackgroundVolume] = useState(0.3);
  const [voiceFadeIn, setVoiceFadeIn] = useState(0);
  const [voiceFadeOut, setVoiceFadeOut] = useState(0);
  const [backgroundFadeIn, setBackgroundFadeIn] = useState(0);
  const [backgroundFadeOut, setBackgroundFadeOut] = useState(2);
  const [voiceDelay, setVoiceDelay] = useState(0); // seconds before voice starts after background
  
  // Mixed audio state
  const [mixedAudio, setMixedAudio] = useState(null);
  const [isMixing, setIsMixing] = useState(false);
  
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingSource, setPlayingSource] = useState(null); // 'voice', 'mixed'
  const audioRef = useRef(null);
  
  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [announcementName, setAnnouncementName] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [announcementType, setAnnouncementType] = useState('General');
  const [isSaving, setIsSaving] = useState(false);
  
  // Settings expanded state
  const [voiceSettingsOpen, setVoiceSettingsOpen] = useState(false);
  const [mixSettingsOpen, setMixSettingsOpen] = useState(true);

  useEffect(() => {
    fetchVoices();
    fetchBackgroundMusic();
    fetchBusinesses();
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const fetchVoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tts/voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoices(response.data.voices || []);
      if (response.data.voices?.length > 0) {
        setSelectedVoice(response.data.voices[0].voice_id);
      }
    } catch (error) {
      console.error('Error fetching voices:', error);
      toast.error('Failed to fetch voices');
    }
  };

  const fetchBackgroundMusic = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/background-music`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSongs(response.data || []);
    } catch (error) {
      console.error('Error fetching background music:', error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data || []);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  // Preview voice sample
  const handlePreviewVoice = (voice) => {
    if (voice.preview_url) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(voice.preview_url);
      audioRef.current = audio;
      audio.play();
      toast.info(`Playing preview of ${voice.name}`);
    } else {
      toast.info('No preview available for this voice');
    }
  };

  const handleGenerate = async () => {
    if (!text.trim()) {
      toast.error('Please enter text to convert');
      return;
    }
    if (!selectedVoice) {
      toast.error('Please select a voice');
      return;
    }

    setIsGenerating(true);
    setGeneratedAudio(null);
    setMixedAudio(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/tts/generate`, {
        text: text.trim(),
        voice_id: selectedVoice,
        stability,
        similarity_boost: similarityBoost,
        style,
        use_speaker_boost: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGeneratedAudio(response.data);
      toast.success('Voice generated successfully!');
    } catch (error) {
      console.error('Error generating TTS:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate voice');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleMixAudio = async () => {
    if (!generatedAudio) {
      toast.error('Please generate voice first');
      return;
    }

    setIsMixing(true);
    
    try {
      const token = localStorage.getItem('token');
      
      let backgroundPath = null;
      if (backgroundSource === 'library' && selectedBackground) {
        backgroundPath = selectedBackground.file_path;
      } else if (backgroundSource === 'upload' && uploadedBackground) {
        backgroundPath = uploadedBackground;
      }
      
      const response = await axios.post(`${API}/tts/mix-audio`, {
        voice_audio_path: generatedAudio.audio_url,
        background_audio_path: backgroundPath,
        voice_volume: voiceVolume,
        background_volume: backgroundVolume,
        voice_fade_in: voiceFadeIn,
        voice_fade_out: voiceFadeOut,
        background_fade_in: backgroundFadeIn,
        background_fade_out: backgroundFadeOut,
        voice_delay: voiceDelay
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMixedAudio(response.data);
      toast.success('Audio mixed successfully!');
    } catch (error) {
      console.error('Error mixing audio:', error);
      toast.error(error.response?.data?.detail || 'Failed to mix audio');
    } finally {
      setIsMixing(false);
    }
  };

  const handlePlay = (source) => {
    const audioUrl = source === 'voice' 
      ? `${BACKEND_URL}${generatedAudio.audio_url}`
      : `${BACKEND_URL}${mixedAudio.audio_url}`;
    
    if (isPlaying && playingSource === source) {
      audioRef.current?.pause();
      setIsPlaying(false);
      setPlayingSource(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setPlayingSource(null);
      });
      
      audio.play();
      setIsPlaying(true);
      setPlayingSource(source);
    }
  };

  const handleSave = async () => {
    if (!announcementName.trim()) {
      toast.error('Please enter a name for the announcement');
      return;
    }
    if (!selectedBusiness) {
      toast.error('Please select a business');
      return;
    }

    setIsSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const audioToSave = mixedAudio || generatedAudio;
      
      await axios.post(`${API}/tts/save-to-announcements`, {
        name: announcementName.trim(),
        text: text,
        voice_id: selectedVoice,
        audio_path: audioToSave.audio_url,
        business_id: selectedBusiness,
        genre: announcementType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Saved to announcements successfully!');
      setSaveDialogOpen(false);
      setAnnouncementName('');
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error(error.response?.data?.detail || 'Failed to save announcement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.mp3')) {
      toast.error('Only MP3 files are allowed');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('artist', 'Background');
    formData.append('genre', 'Background');
    formData.append('duration', 0);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/songs/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadedBackground(response.data.file_path);
      setBackgroundSource('upload');
      toast.success('Background music uploaded');
    } catch (error) {
      toast.error('Failed to upload background music');
    }
  };

  const announcementTypes = [
    'Store Opening', 'Store Closing', 'Promotion', 'Safety', 
    'General', 'Holiday', 'Event', 'Other'
  ];

  return (
    <div className={`p-8 min-h-screen ${isLight ? 'bg-[#faf8f5]' : ''}`}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold mb-2 ${isLight ? 'text-gray-800' : 'text-white'}`} data-testid="tts-title">
              Text to Voice
            </h1>
            <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>Generate professional voice announcements with AI</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Text Input & Voice Selection */}
          <div className="space-y-6">
            {/* Text Input */}
            <div className={`rounded-2xl p-6 space-y-4 ${isLight ? 'bg-white border border-[#ebe7e0]' : 'glass-card'}`}>
              <Label className={`font-medium ${isLight ? 'text-gray-700' : 'text-white'}`}>Enter your announcement text</Label>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type or paste your announcement text here..."
                className={`min-h-[200px] resize-none ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}`}
                data-testid="tts-text-input"
              />
              <p className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>{text.length} characters</p>
            </div>

            {/* Voice Selection */}
            <div className={`rounded-2xl p-6 space-y-4 ${isLight ? 'bg-white border border-[#ebe7e0]' : 'glass-card'}`}>
              <Label className={`font-medium ${isLight ? 'text-gray-700' : 'text-white'}`}>Select Voice</Label>
              <div className="flex gap-2">
                <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                  <SelectTrigger className={`flex-1 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}`} data-testid="voice-select">
                    <SelectValue placeholder="Select a voice" />
                  </SelectTrigger>
                  <SelectContent className={`max-h-[300px] ${isLight ? 'bg-white border-[#ebe7e0]' : 'bg-slate-900 border-slate-700'}`}>
                    {voices.map((voice) => (
                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                        <div className="flex items-center gap-2">
                          <Mic className={`w-4 h-4 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                          <span>{voice.name}</span>
                          <span className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>({voice.category})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Voice Preview Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const voice = voices.find(v => v.voice_id === selectedVoice);
                    if (voice) handlePreviewVoice(voice);
                  }}
                  disabled={!selectedVoice}
                  className={isLight ? 'border-[#ebe7e0] hover:bg-gray-50' : 'border-slate-700 hover:bg-slate-800'}
                  title="Preview voice"
                >
                  <Play className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500">Click the play button to preview the selected voice before generating.</p>

              {/* Voice Settings */}
              <Collapsible open={voiceSettingsOpen} onOpenChange={setVoiceSettingsOpen}>
                <CollapsibleTrigger className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
                  <Sliders className="w-4 h-4" />
                  Voice Settings
                  <ChevronDown className={`w-4 h-4 transition-transform ${voiceSettingsOpen ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4 space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Stability</span>
                      <span className="text-white">{stability.toFixed(2)}</span>
                    </div>
                    <Slider
                      value={[stability]}
                      onValueChange={([v]) => setStability(v)}
                      min={0}
                      max={1}
                      step={0.05}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Clarity + Similarity</span>
                      <span className="text-white">{similarityBoost.toFixed(2)}</span>
                    </div>
                    <Slider
                      value={[similarityBoost]}
                      onValueChange={([v]) => setSimilarityBoost(v)}
                      min={0}
                      max={1}
                      step={0.05}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Style Exaggeration</span>
                      <span className="text-white">{style.toFixed(2)}</span>
                    </div>
                    <Slider
                      value={[style]}
                      onValueChange={([v]) => setStyle(v)}
                      min={0}
                      max={1}
                      step={0.05}
                      className="w-full"
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !text.trim() || !selectedVoice}
                className="w-full bg-rose-600 hover:bg-rose-500 text-white h-12"
                data-testid="generate-button"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 mr-2" />
                    Generate Voice
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Right Column - Preview & Mix */}
          <div className="space-y-6">
            {/* Generated Voice Preview */}
            <div className="glass-card rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-white font-medium">Generated Voice</Label>
                {generatedAudio && (
                  <Button
                    onClick={() => handlePlay('voice')}
                    variant="ghost"
                    size="sm"
                    className="text-rose-400 hover:text-rose-300"
                    data-testid="play-voice-button"
                  >
                    {isPlaying && playingSource === 'voice' ? (
                      <Square className="w-4 h-4 mr-1" />
                    ) : (
                      <Play className="w-4 h-4 mr-1" />
                    )}
                    {isPlaying && playingSource === 'voice' ? 'Stop' : 'Play'}
                  </Button>
                )}
              </div>
              
              {generatedAudio ? (
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-rose-600/20 flex items-center justify-center">
                      <Mic className="w-6 h-6 text-rose-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-white font-medium">Voice Generated</p>
                      <p className="text-xs text-slate-400 truncate">{text.substring(0, 50)}...</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800/30 rounded-xl p-8 text-center">
                  <Mic className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                  <p className="text-slate-500">No voice generated yet</p>
                  <p className="text-xs text-slate-600 mt-1">Enter text and click Generate</p>
                </div>
              )}
            </div>

            {/* Background Music Selection */}
            <div className="glass-card rounded-2xl p-6 space-y-4">
              <Collapsible open={mixSettingsOpen} onOpenChange={setMixSettingsOpen}>
                <CollapsibleTrigger className="flex items-center justify-between w-full">
                  <Label className="text-white font-medium flex items-center gap-2">
                    <Music className="w-4 h-4" />
                    Background Music & Mixing
                  </Label>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${mixSettingsOpen ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                
                <CollapsibleContent className="pt-4 space-y-4">
                  {/* Background Source Selection */}
                  <div className="flex gap-2">
                    <Button
                      variant={backgroundSource === 'none' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBackgroundSource('none')}
                      className={backgroundSource === 'none' ? 'bg-rose-600' : 'border-slate-700'}
                    >
                      No Background
                    </Button>
                    <Button
                      variant={backgroundSource === 'library' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBackgroundSource('library')}
                      className={backgroundSource === 'library' ? 'bg-rose-600' : 'border-slate-700'}
                    >
                      From Library
                    </Button>
                    <Button
                      variant={backgroundSource === 'upload' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBackgroundSource('upload')}
                      className={backgroundSource === 'upload' ? 'bg-rose-600' : 'border-slate-700'}
                    >
                      Upload New
                    </Button>
                  </div>

                  {/* Library Selection */}
                  {backgroundSource === 'library' && (
                    <Select 
                      value={selectedBackground?.id || ''} 
                      onValueChange={(id) => setSelectedBackground(songs.find(s => s.id === id))}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-white">
                        <SelectValue placeholder="Select background music" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 max-h-[200px]">
                        {songs.map((song) => (
                          <SelectItem key={song.id} value={song.id}>
                            <div className="flex items-center gap-2">
                              <Music className="w-4 h-4 text-amber-400" />
                              <span>{song.name}</span>
                              <span className="text-xs text-slate-500">- {song.artist}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {/* Upload Option */}
                  {backgroundSource === 'upload' && (
                    <div>
                      <input
                        type="file"
                        accept=".mp3"
                        onChange={handleBackgroundUpload}
                        className="hidden"
                        id="background-upload"
                      />
                      <label
                        htmlFor="background-upload"
                        className="flex items-center justify-center gap-2 w-full h-20 border-2 border-dashed border-slate-700 rounded-xl cursor-pointer hover:border-rose-500 transition-colors"
                      >
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-slate-400">
                          {uploadedBackground ? 'Background uploaded ✓' : 'Click to upload MP3'}
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Volume Controls */}
                  {backgroundSource !== 'none' && (
                    <div className="space-y-4 pt-2 border-t border-slate-700/50">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-slate-400 flex items-center gap-1">
                            <Volume2 className="w-3 h-3" /> Voice Volume
                          </span>
                          <span className="text-white">{Math.round(voiceVolume * 100)}%</span>
                        </div>
                        <Slider
                          value={[voiceVolume]}
                          onValueChange={([v]) => setVoiceVolume(v)}
                          min={0}
                          max={1.5}
                          step={0.05}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-slate-400 flex items-center gap-1">
                            <Music className="w-3 h-3" /> Background Volume
                          </span>
                          <span className="text-white">{Math.round(backgroundVolume * 100)}%</span>
                        </div>
                        <Slider
                          value={[backgroundVolume]}
                          onValueChange={([v]) => setBackgroundVolume(v)}
                          min={0}
                          max={1}
                          step={0.05}
                          className="w-full"
                        />
                      </div>

                      {/* Fade Controls */}
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div>
                          <p className="text-xs text-slate-500 mb-2">Voice Fade In (sec)</p>
                          <Input
                            type="number"
                            value={voiceFadeIn}
                            onChange={(e) => setVoiceFadeIn(parseFloat(e.target.value) || 0)}
                            min={0}
                            max={10}
                            step={0.5}
                            className="bg-slate-950 border-slate-700 text-white h-8"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-2">Voice Fade Out (sec)</p>
                          <Input
                            type="number"
                            value={voiceFadeOut}
                            onChange={(e) => setVoiceFadeOut(parseFloat(e.target.value) || 0)}
                            min={0}
                            max={10}
                            step={0.5}
                            className="bg-slate-950 border-slate-700 text-white h-8"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-2">BG Fade In (sec)</p>
                          <Input
                            type="number"
                            value={backgroundFadeIn}
                            onChange={(e) => setBackgroundFadeIn(parseFloat(e.target.value) || 0)}
                            min={0}
                            max={10}
                            step={0.5}
                            className="bg-slate-950 border-slate-700 text-white h-8"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-2">BG Fade Out (sec)</p>
                          <Input
                            type="number"
                            value={backgroundFadeOut}
                            onChange={(e) => setBackgroundFadeOut(parseFloat(e.target.value) || 0)}
                            min={0}
                            max={10}
                            step={0.5}
                            className="bg-slate-950 border-slate-700 text-white h-8"
                          />
                        </div>
                      </div>
                      
                      {/* Voice Delay Control */}
                      <div className="pt-3 border-t border-slate-700/50">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-slate-400 flex items-center gap-1">
                            Voice Start Delay
                          </span>
                          <span className="text-white">{voiceDelay}s after BG</span>
                        </div>
                        <Slider
                          value={[voiceDelay]}
                          onValueChange={([v]) => setVoiceDelay(v)}
                          min={0}
                          max={10}
                          step={0.5}
                          className="w-full"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Voice will start {voiceDelay} seconds after background music begins
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Mix Button */}
                  <Button
                    onClick={handleMixAudio}
                    disabled={!generatedAudio || isMixing}
                    className="w-full bg-amber-600 hover:bg-amber-500 text-white"
                    data-testid="mix-button"
                  >
                    {isMixing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Mixing...
                      </>
                    ) : (
                      <>
                        <Music className="w-4 h-4 mr-2" />
                        {backgroundSource === 'none' ? 'Apply Fade Effects' : 'Mix with Background'}
                      </>
                    )}
                  </Button>
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Mixed Audio Preview */}
            {mixedAudio && (
              <div className="glass-card rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-white font-medium">Final Preview</Label>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handlePlay('mixed')}
                      variant="ghost"
                      size="sm"
                      className="text-amber-400 hover:text-amber-300"
                      data-testid="play-mixed-button"
                    >
                      {isPlaying && playingSource === 'mixed' ? (
                        <Square className="w-4 h-4 mr-1" />
                      ) : (
                        <Play className="w-4 h-4 mr-1" />
                      )}
                      {isPlaying && playingSource === 'mixed' ? 'Stop' : 'Play'}
                    </Button>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-amber-600/20 to-rose-600/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center">
                      <Volume2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-white font-medium">Mixed Audio Ready</p>
                      <p className="text-xs text-slate-400">{mixedAudio.duration?.toFixed(1)}s duration</p>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <Button
                  onClick={() => setSaveDialogOpen(true)}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="save-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save to Announcements
                </Button>
              </div>
            )}

            {/* Save without mixing if voice is generated */}
            {generatedAudio && !mixedAudio && (
              <Button
                onClick={() => setSaveDialogOpen(true)}
                variant="outline"
                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Voice Only (No Background)
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle>Save Announcement</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Announcement Name</Label>
              <Input
                value={announcementName}
                onChange={(e) => setAnnouncementName(e.target.value)}
                placeholder="e.g., Welcome Message"
                className="bg-slate-950 border-slate-700 text-white mt-1"
                data-testid="announcement-name-input"
              />
            </div>

            <div>
              <Label>Assign to Business</Label>
              <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1" data-testid="business-select">
                  <SelectValue placeholder="Select a business" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {businesses.map((business) => (
                    <SelectItem key={business.id} value={business.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-cyan-400" />
                        {business.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Announcement Type</Label>
              <Select value={announcementType} onValueChange={setAnnouncementType}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {announcementTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setSaveDialogOpen(false)}
                className="flex-1 border-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || !announcementName.trim() || !selectedBusiness}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                data-testid="confirm-save-button"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TextToVoice;
